import type { ShopState } from '@origami/shared'

/**
 * Transient HP/ATK delta to render as a floating number over a board
 * slot. Queued by each store's `applyState` / `applySession` when it
 * detects a stat increase on a unit that kept its identity (same
 * unit_id + same star + same slot). Shop.tsx reads this list and
 * animates one floater per entry; the floater clears itself when its
 * animation finishes via `clearBuffFloater(id)`.
 */
export interface BuffFloater {
  id: string
  where: 'active' | 'bench'
  position: number
  /** Kind discriminator. 'hp' / 'atk' are the standard +N pops; the
   *  catalog-specific kinds are heuristically inferred by
   *  diffBoardForFloaters from the (prev, next) ShopState pair:
   *    - 'consumed': anchor slot went from a unit → null while the
   *      slot one back grew accruedBench (wolf_spider eating a
   *      neighbor). Renders as a "GOBBLED" paper-fade at the empty
   *      slot. `label` carries the consumed unit's display name.
   *    - 'tribute': a banky_pig at this slot grew accruedBench
   *      while total gold fell. Renders as a "−Ng" coin tag pop at
   *      the slot. `delta` is the gold cost. */
  stat: 'hp' | 'atk' | 'consumed' | 'tribute'
  delta: number
  spawnedAt: number
  /** Optional human-readable label — used by 'consumed' to show the
   *  eaten unit's name. */
  label?: string
  /** Earliest `performance.now()` value at which the floater should
   *  start animating. Set on combine-triggered floaters
   *  (`on_combine_witnessed` buffs to other allies) so they don't
   *  render under the EvolutionSequence's fullscreen scrim — the
   *  floater div mounts immediately with `animation-delay` so the
   *  +N pop appears the moment the overlay finishes. */
  notBeforeMs?: number
}

let floaterIdCounter = 0
const nextFloaterId = () => `bf-${Date.now().toString(36)}-${++floaterIdCounter}`

/**
 * Compare two consecutive ShopState snapshots and return a list of
 * BuffFloaters for board units whose stats climbed while keeping their
 * identity. Identity = (unit_id, star) at a stable (where, position)
 * tuple. The identity gate avoids spurious floaters on:
 *   - reorder (slot changes but identity stays — diff still triggers
 *     because we key by slot; reorders rarely change stats anyway)
 *   - combine star-up (star changes, so identity mismatch → no floater)
 *   - sell (unit removed, no identity to match)
 *   - buy (new unit, no identity to match)
 * Captures: on_purchase / on_combine_witnessed / on_shop_round_pass
 * (sage_owl) / walking_stick accrual deltas land here.
 */
export function diffBoardForFloaters(
  prevShop: ShopState,
  nextShop: ShopState,
  opts?: { delayMs?: number },
): BuffFloater[] {
  const floaters: BuffFloater[] = []
  const now = performance.now()
  const notBeforeMs =
    opts?.delayMs && opts.delayMs > 0 ? now + opts.delayMs : undefined
  const lanes: Array<{ where: 'active' | 'bench'; prev: ShopState['activeLine']; next: ShopState['activeLine'] }> = [
    { where: 'active', prev: prevShop.activeLine, next: nextShop.activeLine },
    { where: 'bench', prev: prevShop.bench, next: nextShop.bench },
  ]
  for (const { where, prev: prevArr, next: nextArr } of lanes) {
    const len = Math.min(prevArr.length, nextArr.length)
    for (let i = 0; i < len; i++) {
      const p = prevArr[i]
      const n = nextArr[i]
      // Consume detection: a slot that went from a unit to null while
      // the slot directly BEHIND it (index + 1 — wolf_spider sits one
      // back) grew accruedBench. Active line only — bench doesn't
      // host consume_unit. Emit a 'GOBBLED' floater at the emptied
      // slot. Heuristic rather than server-emitted event; reliable
      // because consume_unit is the only mechanism today that empties
      // a slot during a state transition without removing the
      // neighbor.
      if (where === 'active' && p && !n) {
        const behind = nextArr[i + 1]
        const behindPrev = prevArr[i + 1]
        if (behind && behindPrev && grewAccrued(behindPrev, behind)) {
          floaters.push({
            id: nextFloaterId(),
            where,
            position: i,
            stat: 'consumed',
            delta: 0,
            label: p.unit.name,
            spawnedAt: now,
            notBeforeMs,
          })
        }
      }
      if (!p || !n) continue
      if (p.unit.id !== n.unit.id) continue
      if (p.star !== n.star) continue
      if (n.hp > p.hp) {
        floaters.push({
          id: nextFloaterId(),
          where,
          position: i,
          stat: 'hp',
          delta: n.hp - p.hp,
          spawnedAt: now,
          notBeforeMs,
        })
      }
      if (n.atk > p.atk) {
        floaters.push({
          id: nextFloaterId(),
          where,
          position: i,
          stat: 'atk',
          delta: n.atk - p.atk,
          spawnedAt: now,
          notBeforeMs,
        })
      }
      // Tribute detection: a banky_pig at this slot grew accruedBench
      // (i.e. it paid this round's tribute) AND the player's total
      // gold fell. Pin the −Ng tag to banky's slot. Coupling to the
      // unit_id is acceptable here — gold_tribute_buff is unit-specific
      // and the floater is the visual contract for it.
      if (
        n.unit.id === 'banky_pig' &&
        grewAccrued(p, n) &&
        nextShop.gold < prevShop.gold
      ) {
        floaters.push({
          id: nextFloaterId(),
          where,
          position: i,
          stat: 'tribute',
          delta: prevShop.gold - nextShop.gold,
          spawnedAt: now,
          notBeforeMs,
        })
      }
    }
  }
  return floaters
}

function grewAccrued(
  prev: { accruedBench?: { hp: number; atk: number } },
  next: { accruedBench?: { hp: number; atk: number } },
): boolean {
  const a = prev.accruedBench ?? { hp: 0, atk: 0 }
  const b = next.accruedBench ?? { hp: 0, atk: 0 }
  return b.hp > a.hp || b.atk > a.atk
}
